import { useState, useEffect, useRef } from 'react'
import { Select, PageHelperText, message, Button, IconButton, ConfirmModal } from '@/components/ui'
import { PlusCircleFilled, EditOutlined, DeleteOutlined, UserOutlined, EnvironmentOutlined, HolderOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { router } from '@inertiajs/react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core'
import type {
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  CollisionDetection,
  UniqueIdentifier,
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import axios from '@/lib/axios'
import dayjs from 'dayjs'
import { ZeroState } from '@/components/patterns'
import SessionDrawer from './SessionDrawer'

/** Prefix used for each track column's droppable id, so cards can be dropped onto empty columns. */
const COLUMN_ID_PREFIX = 'track-'

/**
 * Multi-column collision detection: prefer the droppable the pointer is directly within (so hovering
 * anywhere over a column — including its empty space — resolves that column), then fall back to
 * rectangle intersection when the pointer is over a gap. `closestCorners` alone can miss a distant
 * column, which is what blocked cross-column drops.
 */
const collisionDetectionStrategy: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args)
  if (pointerCollisions.length > 0) {
    return pointerCollisions
  }
  return rectIntersection(args)
}

interface Event {
  id: number
  name: string
  slug: string
}

interface Track {
  id: number
  name: string
  color: string
  position: number
}

interface AgendaDay {
  id: number
  scheduled_on: string
  position: number
  tracks: Track[]
}

interface SessionSpeaker {
  id: number
  full_name: string
}

interface Session {
  id: number
  title: string
  description: string | null
  session_date: string
  start_time: string | null
  end_time: string | null
  visible: boolean
  agenda_day_id: number
  agenda_track_id: number
  agenda_venue_id: number | null
  venue_name: string | null
  track_color: string | null
  position: number
  speakers: SessionSpeaker[]
}

interface Speaker {
  id: number
  full_name: string
}

interface Venue {
  id: number
  title: string
}

interface BuilderTabProps {
  event: Event
  agendaDays: AgendaDay[]
  sessions: Session[]
  speakers: Speaker[]
  venues: Venue[]
  /** Preselect this day when the Builder opens (falls back to the first day when omitted). */
  initialDayId?: number
  /** When set, renders a "Back to Tracks" control above the day selector. */
  onBack?: () => void
}

/** Shape returned by the session reorder endpoint. */
interface ReorderResponse {
  success: boolean
  sessions: Session[]
  errors?: string[]
}

interface SessionCardProps {
  session: Session
  /** Left-border colour, sourced from the destination track so cross-track moves recolour instantly. */
  trackColor: string
  onEdit: (session: Session) => void
  onDelete: (sessionId: number, sessionName: string) => void
  /** Optional drag handle rendered at the start of the header row when reordering is enabled. */
  dragHandle?: React.ReactNode
}

/** Presentational session card: time range, title, venue/speaker chips and edit/delete actions. */
function SessionCard({ session, trackColor, onEdit, onDelete, dragHandle }: SessionCardProps) {
  return (
    <div
      className={`border-l-4 p-3 hover:shadow-md transition-shadow group ${session.visible ? 'bg-white' : 'bg-gray-100'}`}
      style={{ borderLeftColor: session.visible ? trackColor : '#9ca3af' }}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex items-center gap-1 min-w-0">
          {dragHandle}
          <div className="text-sm font-medium text-gray-900">
            {session.start_time && session.end_time ? (
              `${session.start_time} - ${session.end_time}`
            ) : session.start_time ? (
              session.start_time
            ) : session.end_time ? (
              session.end_time
            ) : (
              <span className="text-gray-400">Time not set</span>
            )}
          </div>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <IconButton
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit(session)}
            ariaLabel="Edit session"
            title="Edit"
          />
          <IconButton
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(session.id, session.title)}
            ariaLabel="Delete session"
            title="Delete"
          />
        </div>
      </div>

      <h4 className="font-medium text-gray-900">{session.title}</h4>

      <div className="flex flex-wrap gap-2 mt-2">
        {session.venue_name && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-200 rounded-sm text-xs text-gray-700">
            <EnvironmentOutlined className="text-sm" />
            <span>{session.venue_name}</span>
          </div>
        )}

        {session.speakers.length > 0 && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-200 rounded-sm text-xs text-gray-700">
            <UserOutlined className="text-sm" />
            <span>{session.speakers.map(s => s.full_name).join(', ')}</span>
          </div>
        )}
      </div>

      {!session.visible && (
        <div className="text-xs text-gray-600 italic mt-2">Not visible to public</div>
      )}
    </div>
  )
}

interface SortableSessionCardProps {
  session: Session
  trackColor: string
  onEdit: (session: Session) => void
  onDelete: (sessionId: number, sessionName: string) => void
}

/** Wraps a SessionCard with @dnd-kit sortable behaviour and a dedicated drag handle. */
function SortableSessionCard({ session, trackColor, onEdit, onDelete }: SortableSessionCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: session.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // A DragOverlay renders the "live" card, so the source keeps a dimmed placeholder in place.
    opacity: isDragging ? 0.4 : 1,
  }

  // Listeners live on the handle only, so edit/delete clicks and (empty-cell) add clicks stay intact.
  const dragHandle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      className="shrink-0 px-1 py-0.5 -ml-1 text-gray-400 hover:text-gray-700 cursor-move touch-none flex items-center"
      aria-label="Drag to reorder"
      {...attributes}
      {...listeners}
    >
      <HolderOutlined />
    </button>
  )

  return (
    <div ref={setNodeRef} style={style}>
      <SessionCard
        session={session}
        trackColor={trackColor}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandle={dragHandle}
      />
    </div>
  )
}

interface TrackColumnProps {
  track: Track
  scheduledOn: string
  /** This track's sessions for the selected day, already ordered by position. */
  sessions: Session[]
  onCreate: (trackId: number) => void
  onEdit: (session: Session) => void
  onDelete: (sessionId: number, sessionName: string) => void
}

/**
 * A single track column. Its content area is a droppable container (so cards can land in an EMPTY or
 * short column, anywhere over its height) and wraps its cards in a SortableContext scoped to this
 * track's session ids.
 */
function TrackColumn({ track, scheduledOn, sessions, onCreate, onEdit, onDelete }: TrackColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `${COLUMN_ID_PREFIX}${track.id}` })

  return (
    <div className="border border-gray-200 flex flex-col">
      <div
        className="p-4 font-semibold text-gray-900 flex items-center gap-2"
        style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}
      >
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: track.color }}
        />
        <span>{dayjs(scheduledOn).format('D MMM YYYY')} | {track.name}</span>
      </div>

      <SortableContext items={sessions.map(s => s.id)} strategy={verticalListSortingStrategy}>
        {/* The droppable ref wraps the whole content area (min height + flex-1) so short columns stay a large drop target. */}
        <div
          ref={setNodeRef}
          className={`flex-1 p-3 space-y-3 min-h-[240px] transition-colors ${isOver ? 'bg-blue-50' : ''}`}
        >
          {sessions.length === 0 ? (
            <button
              onClick={() => onCreate(track.id)}
              className="w-full h-32 border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center text-gray-400 hover:text-blue-600"
            >
              <PlusCircleFilled className="text-2xl" />
            </button>
          ) : (
            <>
              {sessions.map(session => (
                <SortableSessionCard
                  key={session.id}
                  session={session}
                  trackColor={track.color}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}

              <button
                onClick={() => onCreate(track.id)}
                className="w-full h-12 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center text-gray-400 hover:text-blue-600"
              >
                <PlusCircleFilled />
              </button>
            </>
          )}
        </div>
      </SortableContext>
    </div>
  )
}

export default function BuilderTab({
  event,
  agendaDays,
  sessions,
  speakers,
  venues,
  initialDayId,
  onBack,
}: BuilderTabProps) {
  const [selectedDayId, setSelectedDayId] = useState<number | null>(
    initialDayId ?? (agendaDays.length > 0 ? agendaDays[0].id : null)
  )
  // Local, optimistic copy of sessions so drag-and-drop can update instantly, then reconcile with the server.
  const [localSessions, setLocalSessions] = useState<Session[]>(sessions)
  // Id of the card currently being dragged (drives the DragOverlay).
  const [activeId, setActiveId] = useState<number | null>(null)
  // Snapshot of state taken at drag start; used to revert since onDragOver mutates state mid-drag.
  const preDragSnapshot = useRef<Session[] | null>(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [editingSession, setEditingSession] = useState<Session | null>(null)
  const [preselectedTrackId, setPreselectedTrackId] = useState<number | null>(null)
  const [deleteModal, setDeleteModal] = useState<{ visible: boolean; sessionId: number | null; sessionName: string }>({
    visible: false,
    sessionId: null,
    sessionName: ''
  })

  // Resync when the sessions prop changes (e.g. after SessionDrawer save / delete triggers router.reload).
  useEffect(() => {
    setLocalSessions(sessions)
  }, [sessions])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const selectedDay = agendaDays.find(day => day.id === selectedDayId)

  const activeSession = activeId != null ? localSessions.find(s => s.id === activeId) ?? null : null
  const activeTrackColor =
    activeSession && selectedDay
      ? selectedDay.tracks.find(t => t.id === activeSession.agenda_track_id)?.color ?? '#9ca3af'
      : '#9ca3af'

  const handleCreateSession = (trackId: number) => {
    setPreselectedTrackId(trackId)
    setEditingSession(null)
    setDrawerVisible(true)
  }

  const handleEditSession = (session: Session) => {
    setPreselectedTrackId(null)
    setEditingSession(session)
    setDrawerVisible(true)
  }

  const handleDeleteSessionClick = (sessionId: number, sessionName: string) => {
    setDeleteModal({ visible: true, sessionId, sessionName })
  }

  const handleDeleteSession = async () => {
    if (!deleteModal.sessionId) return
    try {
      const response = await axios.delete(`/events/${event.slug}/agenda/sessions/${deleteModal.sessionId}`)

      if (response.data.success) {
        message.success(response.data.message)
        router.reload()
      }
    } catch (error: any) {
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to delete session')
    } finally {
      setDeleteModal({ visible: false, sessionId: null, sessionName: '' })
    }
  }

  /**
   * Resolve the track (container) that a droppable/draggable id belongs to.
   * `track-*` ids map straight to that track; a session id maps to that session's CURRENT track in
   * local state (which reflects any mid-drag cross-track move performed by onDragOver).
   */
  const findContainer = (id: UniqueIdentifier): number | null => {
    if (typeof id === 'string' && id.startsWith(COLUMN_ID_PREFIX)) {
      return Number(id.slice(COLUMN_ID_PREFIX.length))
    }
    const session = localSessions.find(s => s.id === Number(id))
    return session ? session.agenda_track_id : null
  }

  const handleDragStart = (dragEvent: DragStartEvent) => {
    setActiveId(Number(dragEvent.active.id))
    preDragSnapshot.current = localSessions
  }

  /**
   * Cross-container hand-off: while a card hovers a DIFFERENT column, move it into that column's list
   * in local state so it becomes part of that column's SortableContext. Without this, `over` never
   * resolves to the other column and the drop is rejected.
   */
  const handleDragOver = (dragEvent: DragOverEvent) => {
    const { active, over } = dragEvent
    if (!over) return

    const activeContainer = findContainer(active.id)
    const overContainer = findContainer(over.id)
    if (activeContainer == null || overContainer == null || activeContainer === overContainer) return

    const overId = over.id
    const isOverColumn = typeof overId === 'string' && overId.startsWith(COLUMN_ID_PREFIX)
    const activeRect = active.rect.current.translated
    const overRect = over.rect

    setLocalSessions(prev => {
      const dragged = prev.find(s => s.id === Number(active.id))
      if (!dragged) return prev

      const destList = prev
        .filter(s => s.agenda_day_id === selectedDayId && s.agenda_track_id === overContainer && s.id !== dragged.id)
        .sort((a, b) => a.position - b.position)

      let newIndex: number
      if (isOverColumn) {
        newIndex = destList.length
      } else {
        const overIndex = destList.findIndex(s => s.id === Number(overId))
        if (overIndex === -1) {
          newIndex = destList.length
        } else {
          // Drop below the hovered card's midpoint → insert after it, otherwise before.
          const isBelow = activeRect ? activeRect.top > overRect.top + overRect.height / 2 : false
          newIndex = overIndex + (isBelow ? 1 : 0)
        }
      }

      const newDest = [...destList]
      newDest.splice(newIndex, 0, dragged)

      const sourceList = prev
        .filter(s => s.agenda_day_id === selectedDayId && s.agenda_track_id === activeContainer && s.id !== dragged.id)
        .sort((a, b) => a.position - b.position)

      const destPos = new Map<number, number>()
      newDest.forEach((s, index) => destPos.set(s.id, index))
      const sourcePos = new Map<number, number>()
      sourceList.forEach((s, index) => sourcePos.set(s.id, index))

      return prev.map(s => {
        if (destPos.has(s.id)) {
          return { ...s, agenda_track_id: overContainer, position: destPos.get(s.id)! }
        }
        if (sourcePos.has(s.id)) {
          return { ...s, agenda_track_id: activeContainer, position: sourcePos.get(s.id)! }
        }
        return s
      })
    })
  }

  const handleDragEnd = async (dragEvent: DragEndEvent) => {
    const { active, over } = dragEvent
    const snapshot = preDragSnapshot.current
    preDragSnapshot.current = null
    setActiveId(null)

    // Dropped outside any droppable → undo the mid-drag optimistic move.
    if (!over) {
      if (snapshot) setLocalSessions(snapshot)
      return
    }

    const activeId = Number(active.id)
    const currentActive = localSessions.find(s => s.id === activeId)
    if (!currentActive) {
      if (snapshot) setLocalSessions(snapshot)
      return
    }
    const beforeSession = snapshot?.find(s => s.id === activeId)

    // The card's CURRENT track already reflects any onDragOver hand-off (its real, on-screen column).
    const destTrackId = currentActive.agenda_track_id

    // Did onDragOver relocate the card during this drag? It only runs for cross-column moves, so a
    // changed track/position means the card is already sitting at its final visual (release) index.
    const movedDuringDrag =
      !!beforeSession &&
      (beforeSession.agenda_track_id !== currentActive.agenda_track_id ||
        beforeSession.position !== currentActive.position)

    // Destination track's CURRENT ordered list (post-onDragOver), including the dragged card.
    const destList = localSessions
      .filter(s => s.agenda_day_id === selectedDayId && s.agenda_track_id === destTrackId)
      .sort((a, b) => a.position - b.position)

    let finalList: Session[]
    let targetIndex: number

    if (movedDuringDrag) {
      // Cross-column: FINALISE at the index the card already occupies — do NOT re-append or recompute
      // from `over` (which can resolve to the column container and wrongly send the card to the bottom).
      const currentIndex = destList.findIndex(s => s.id === activeId)
      if (currentIndex === -1) {
        if (snapshot) setLocalSessions(snapshot)
        return
      }
      finalList = destList
      targetIndex = currentIndex
    } else {
      // Same-column reorder: onDragOver was skipped, so local state still holds the pre-drag order.
      // Compute the release slot from `over` and reorder with arrayMove.
      const oldIndex = destList.findIndex(s => s.id === activeId)
      if (oldIndex === -1) {
        if (snapshot) setLocalSessions(snapshot)
        return
      }
      const isOverColumn = typeof over.id === 'string' && over.id.startsWith(COLUMN_ID_PREFIX)
      let newIndex = isOverColumn ? destList.length - 1 : destList.findIndex(s => s.id === Number(over.id))
      if (newIndex === -1) newIndex = destList.length - 1
      if (oldIndex === newIndex) {
        // Dropped exactly where it started → nothing to persist.
        if (snapshot) setLocalSessions(snapshot)
        return
      }
      finalList = arrayMove(destList, oldIndex, newIndex)
      targetIndex = newIndex
    }

    // Finalise positions for the destination track in local state (optimistic).
    const destPos = new Map<number, number>()
    finalList.forEach((s, index) => destPos.set(s.id, index))
    setLocalSessions(prev =>
      prev.map(s =>
        destPos.has(s.id) ? { ...s, agenda_track_id: destTrackId, position: destPos.get(s.id)! } : s
      )
    )

    try {
      const res = await axios.patch<ReorderResponse>(
        `/events/${event.slug}/agenda/sessions/${activeId}/reorder`,
        { agenda_track_id: destTrackId, position: targetIndex }
      )
      if (res.data.success) {
        setLocalSessions(res.data.sessions)
      } else {
        if (snapshot) setLocalSessions(snapshot)
        message.error(res.data.errors?.join(', ') || 'Failed to reorder session')
      }
    } catch (e: any) {
      if (snapshot) setLocalSessions(snapshot)
      message.error(e.response?.data?.errors?.join(', ') || 'Failed to reorder session')
    }
  }

  const handleDragCancel = () => {
    if (preDragSnapshot.current) setLocalSessions(preDragSnapshot.current)
    preDragSnapshot.current = null
    setActiveId(null)
  }

  const dayOptions = agendaDays.map(day => ({
    label: dayjs(day.scheduled_on).format('D MMMM YYYY'),
    value: day.id
  }))

  // Shown only when the Builder is opened in-place from the Tracks tab.
  const backButton = onBack ? (
    <div className="mb-4">
      <Button variant="text" icon={<ArrowLeftOutlined />} onClick={onBack} className="ps-0!">
        Back to Tracks
      </Button>
    </div>
  ) : null

  if (agendaDays.length === 0) {
    return (
      <div>
        {backButton}
        <ZeroState
          icon={<span className="text-6xl">📅</span>}
          title="No Days Available"
          description="Please add dates and tracks in the Tracks tab first"
        />
      </div>
    )
  }

  if (!selectedDay || selectedDay.tracks.length === 0) {
    return (
      <div>
        {backButton}
        {agendaDays.length > 1 && (
          <Select
            value={selectedDayId}
            onChange={setSelectedDayId}
            options={dayOptions}
            className="w-64 mb-6"
          />
        )}
        <ZeroState
          icon={<span className="text-6xl">🎯</span>}
          title="No Tracks Available"
          description="Please add tracks for this day in the Tracks tab"
        />
      </div>
    )
  }

  return (
    <div>
      {backButton}
      {agendaDays.length > 1 && (
        <div className="mb-4">
          <Select
            value={selectedDayId}
            onChange={setSelectedDayId}
            options={dayOptions}
            style={{ width: 200 }}
            block={false}
          />
        </div>
      )}
      <div className="mb-6">
        <PageHelperText>
          To add a session, click on an empty grid below. Drag the handle on a card to reorder or move it between tracks.
        </PageHelperText>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetectionStrategy}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="grid items-stretch" style={{ gridTemplateColumns: `repeat(${selectedDay.tracks.length}, 1fr)` }}>
          {selectedDay.tracks.map(track => {
            const trackSessions = localSessions
              .filter(s => s.agenda_day_id === selectedDayId && s.agenda_track_id === track.id)
              .sort((a, b) => a.position - b.position)

            return (
              <TrackColumn
                key={track.id}
                track={track}
                scheduledOn={selectedDay.scheduled_on}
                sessions={trackSessions}
                onCreate={handleCreateSession}
                onEdit={handleEditSession}
                onDelete={handleDeleteSessionClick}
              />
            )
          })}
        </div>

        {/* Rendered above column boundaries so the dragged card isn't clipped by a column. */}
        <DragOverlay>
          {activeSession ? (
            <div className="shadow-2xl cursor-grabbing">
              <SessionCard
                session={activeSession}
                trackColor={activeTrackColor}
                onEdit={() => {}}
                onDelete={() => {}}
                dragHandle={
                  <span className="shrink-0 px-1 py-0.5 -ml-1 text-gray-500 flex items-center">
                    <HolderOutlined />
                  </span>
                }
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <SessionDrawer
        event={event}
        selectedDay={selectedDay}
        preselectedTrackId={preselectedTrackId}
        session={editingSession}
        speakers={speakers}
        venues={venues}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false)
          setEditingSession(null)
          setPreselectedTrackId(null)
        }}
      />

      <ConfirmModal
        open={deleteModal.visible}
        title="Delete session"
        onConfirm={handleDeleteSession}
        onClose={() => setDeleteModal({ visible: false, sessionId: null, sessionName: '' })}
        confirmText="Yes"
        danger
      >
        Are you sure you want to delete this session?
      </ConfirmModal>
    </div>
  )
}
